import styled from '@emotion/styled'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useBenefitsCarousel } from '../../hooks/useBenefitsCarousel'
import { ButtonLoadingSpinner, LockIcon } from '../icons/Icons'
import { LandingPageLinksContext } from '../../context/LandingPageLinksContext'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { PageContext } from '../../context/PageContextProvider'
import { useLandingPageLinksForHashes } from '../../hooks/useLandingPageLinksForHashes'
import {
    isCartActionButton,
    isCartActionButtonType,
    LP_BUTTON_TYPE,
    parseCombinedAtcBuyNowButtonText,
} from '@storefronts/common-types'
import { resolveCartTrackingVariant } from '../../utils/userEventTools'
import {
    BUTTON_BORDER_RADIUS_VAR,
    CustomizationContext,
    PRIMARY_CTA_BORDER_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    SECONDARY_CTA_BORDER_VAR,
} from '../../context/CustomizationContextProvider'
import { useShopPayButtons } from '../../hooks/useShopPayButtons'
import {
    BenefitsCarouselViewport,
    BenefitSlide,
    BenefitSlideContent,
} from './BenefitsCarousel'
import { ShopPayCTAButton } from './ShopPayCTAButton'
import { AnalyticsContext } from '../../context/analyticsContext'
import { CartContext } from '../../context/cartContext'
import { getProductPrices } from '../../utils/priceFormat'
import { getDefaultPdpSellingPlanId } from '../../utils/pdpSubscriptions'
import {
    getRequiresSellingPlan,
    getSellingPlanGroups,
    resolveDefaultPdpVariant,
} from '../../utils/productDisplay'
import { ctaPulse } from '../../style/ctaPulseAnimation'
import { hoverFill } from '../../style/interactiveStates'
import { FloatingProductSummary } from './FloatingProductSummary'
import { getTypographyRoleStyle } from '../../style/typography'
import { useFitToWidth } from '../../hooks/useFitToWidth'
import {
    STICKY_FOOTER_BENEFITS_CAROUSEL_PADDING_BOTTOM,
    STICKY_FOOTER_BUTTON_AREA_PADDING_BOTTOM,
    STICKY_FOOTER_BUTTON_GAP,
    STICKY_FOOTER_INNER_PADDING,
    STICKY_FOOTER_NON_CART_ACTION_MARGIN,
    getStickyFooterSafeAreaPadding,
} from '../../style/styleConstants'
import {
    getCartActionBrandBenefits,
    hasStickyFooterBrandBenefits,
} from '../../utils/stickyFooterUtils'

type CTAButtonProps = {
    backgroundColor?: string
    textColor?: string
    border?: string
    buttonText: string | React.ReactElement | null
    /** Plain-text label used for fit remeasurement when `buttonText` is a React element. */
    fitLabel?: string
    fontWeight?: number
    fontSize?: number
    onClick: () => void
    padding?: string | number
    disabled?: boolean
    height?: number | string
    width?: number | string
    loader?: boolean
    pulse?: boolean
}

const grey = '#D3D3D3' // TODO: where should this be defined?

const CTA_BUTTON_OWN_PROPS = ['pulse', 'fillColor', 'labelColor', 'disabled']

/**
 * Fill and label live here rather than in the inline style the rest of the
 * button uses, because an inline declaration outranks the hover rule.
 */
const CTAButtonDiv = styled('div', {
    shouldForwardProp: (prop) => !CTA_BUTTON_OWN_PROPS.includes(prop),
})<{
    pulse?: boolean
    fillColor: string
    labelColor: string
    disabled: boolean
}>(({ pulse, fillColor, labelColor, disabled }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    textAlign: 'center',
    pointerEvents: 'auto',
    backgroundColor: disabled ? grey : fillColor,
    color: labelColor,
    ...(!disabled && hoverFill(fillColor, labelColor)),
    ...(pulse && {
        animation: `${ctaPulse} 2.5s ease-in-out infinite`,
        '&:hover': { animationPlayState: 'paused' },
    }),
}))

type LPMainButtonProps = Omit<
    CTAButtonProps,
    'onClick' | 'buttonText' | 'disabled'
>

/**
 * Component handling BUY_NOW and ADD_TO_CART floating button logic and rendering.
 * Both share variant selection, pricing, and product-picker UX — they differ only
 * on click action (immediate checkout vs. open cart drawer).
 */
// eslint-disable-next-line complexity
const LPMainProductActionButton: React.FC<LPMainButtonProps> = (props) => {
    const { landingPage } = useContext(ViddyLPContext)
    const {
        selectedCartActionVariantShopifyId,
        scrollToInlineProductSection,
        setIsCartOpen,
    } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { addCartItem } = useContext(CartContext)
    const {
        primaryCtaTextColor,
        secondaryCtaFillColor,
        secondaryCtaTextColor,
        displaySubscriptionOptions,
        defaultPurchaseOption,
    } = useContext(CustomizationContext)
    const ctaPulseEnabled = landingPage.ctaPulseEnabled ?? false
    const displayShopPayButton = useShopPayButtons()

    const buttonType = landingPage.button?.type
    const isAddToCartOnly = buttonType === LP_BUTTON_TYPE.ADD_TO_CART
    const isBothCartActions =
        buttonType === LP_BUTTON_TYPE.ADD_TO_CART_AND_BUY_NOW

    const brandBenefits = getCartActionBrandBenefits(landingPage, buttonType)
    const benefitsCarouselIndex = useBenefitsCarousel(brandBenefits)

    const productButton = isCartActionButton(landingPage.button)
        ? landingPage.button
        : undefined

    const buttonLinkHash = productButton?.linkHash
    const resolvedLinks = useLandingPageLinksForHashes(
        buttonLinkHash ? [buttonLinkHash] : [],
    )
    const productLpLink = resolvedLinks[0]

    // Memoize computed values
    const selectedVariant = useMemo(() => {
        if (!productLpLink) return null
        return resolveDefaultPdpVariant(productLpLink, {
            initialVariantShopifyId: selectedCartActionVariantShopifyId,
        })
    }, [productLpLink, selectedCartActionVariantShopifyId])

    const disabled = useMemo(() => {
        if (!productLpLink?.variantInfo?.length) return false
        return !selectedVariant?.available
    }, [productLpLink, selectedVariant])

    const priceInfo = useMemo(() => {
        if (!productLpLink) return null
        return getProductPrices(
            productLpLink,
            landingPage.discount || null,
            selectedVariant,
        )
    }, [selectedVariant, productLpLink, landingPage.discount])

    // The brand's default purchase option for this product, applied to the
    // footer's add-to-cart / buy-now even though it shows no subscribe selector.
    // Subscription-only products always get their first plan.
    const defaultSellingPlanId = getDefaultPdpSellingPlanId({
        sellingPlanGroups: productLpLink
            ? getSellingPlanGroups(
                  productLpLink,
                  selectedVariant?.shopifyProductId,
                  selectedVariant?.shopifyId,
              )
            : undefined,
        subscriptionBasePriceCents: selectedVariant?.price.price ?? null,
        defaultPurchaseOption,
        displaySubscriptionOptions,
        requiresSellingPlan: productLpLink
            ? getRequiresSellingPlan(
                  productLpLink,
                  selectedVariant?.shopifyProductId,
              )
            : false,
    })

    // Early return if not a product-action button (after all hooks)
    if (!productButton) {
        return null
    }

    // Don't render if we don't have the necessary data
    if (!productLpLink || !selectedVariant || !priceInfo) {
        return null
    }

    const combinedAtcBnLabels = isBothCartActions
        ? parseCombinedAtcBuyNowButtonText(productButton.buttonText)
        : null

    const resolveTrackingVariant = async () => {
        if (!productButton || !productLpLink) return undefined
        return resolveCartTrackingVariant(
            productLpLink,
            selectedCartActionVariantShopifyId,
        )
    }

    const handleAddToCartClick = async () => {
        const trackingVariant = await resolveTrackingVariant()
        if (!trackingVariant) return
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'email_vlp_primary',
            location: 'footer',
        })
        addCartItem(trackingVariant, 1, defaultSellingPlanId)
        setIsCartOpen(true)
    }

    const handleBuyNowClick = async (shopPay?: boolean) => {
        const trackingVariant = await resolveTrackingVariant()
        if (!trackingVariant) return
        Analytics.TRACK_CART_CHECKOUT_CLICK({ buyNowVariant: trackingVariant })
        addCartItem(trackingVariant, 1, defaultSellingPlanId, true, shopPay)
    }

    const handleShopPayClick = async () => {
        await handleBuyNowClick(true)
    }

    /**
     * Handle click on the product title or variant subtitle.
     * Scrolls to the nearest Inline Product section with this product if one exists.
     */
    const handleProductInfoClick = () => {
        // Use the linkHash from the loaded product data for reliable matching
        const linkHash = productLpLink?.linkHash || buttonLinkHash
        if (!linkHash) return
        scrollToInlineProductSection(linkHash)
    }

    const mainButtonText = isAddToCartOnly ? (
        productButton.buttonText
    ) : (
        <span style={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon color={props.textColor || primaryCtaTextColor} />
            {productButton.buttonText}
        </span>
    )

    const buyNowButtonText = (
        <span style={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon color={props.textColor || primaryCtaTextColor} />
            {combinedAtcBnLabels?.buyNow ?? 'Buy Now'}
        </span>
    )

    const hasVisibleBrandBenefits = hasStickyFooterBrandBenefits(
        brandBenefits,
        !disabled,
    )

    return (
        <div
            style={{
                width: '100%',
                alignSelf: 'flex-end',
                paddingBottom: getStickyFooterSafeAreaPadding(
                    hasVisibleBrandBenefits,
                ),
            }}
        >
            <div
                style={{
                    width: '100%',
                    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
                    boxSizing: 'border-box',
                    padding: STICKY_FOOTER_INNER_PADDING,
                }}
            >
                <FloatingProductSummary
                    lpLink={productLpLink}
                    selectedVariant={selectedVariant}
                    priceInfo={priceInfo}
                    onClick={handleProductInfoClick}
                />

                <div
                    style={{
                        paddingBottom: hasVisibleBrandBenefits
                            ? 0
                            : STICKY_FOOTER_BUTTON_AREA_PADDING_BOTTOM,
                    }}
                >
                    {isBothCartActions ? (
                        <div
                            style={{
                                display: 'flex',
                                gap: STICKY_FOOTER_BUTTON_GAP,
                                width: '100%',
                                alignItems: 'stretch',
                            }}
                        >
                            <div
                                style={{
                                    flex: '1 1 0',
                                    minWidth: 0,
                                }}
                            >
                                <CTAButton
                                    backgroundColor={secondaryCtaFillColor}
                                    textColor={secondaryCtaTextColor}
                                    fontWeight={props.fontWeight}
                                    fontSize={props.fontSize}
                                    border={`var(${SECONDARY_CTA_BORDER_VAR})`}
                                    loader={false}
                                    height={48}
                                    onClick={handleAddToCartClick}
                                    buttonText={combinedAtcBnLabels!.addToCart}
                                    disabled={disabled}
                                />
                            </div>
                            <div
                                style={{
                                    flex: '1 1 0',
                                    minWidth: 0,
                                }}
                            >
                                {displayShopPayButton && !disabled ? (
                                    <ShopPayCTAButton
                                        onClick={() => handleBuyNowClick(true)}
                                        loader={true}
                                        pulse={ctaPulseEnabled}
                                    />
                                ) : (
                                    <CTAButton
                                        backgroundColor={props.backgroundColor}
                                        textColor={props.textColor}
                                        fontWeight={props.fontWeight}
                                        fontSize={props.fontSize}
                                        loader
                                        height={48}
                                        onClick={() => handleBuyNowClick()}
                                        buttonText={buyNowButtonText}
                                        fitLabel={
                                            combinedAtcBnLabels?.buyNow ??
                                            'Buy Now'
                                        }
                                        disabled={disabled}
                                        pulse={ctaPulseEnabled}
                                    />
                                )}
                            </div>
                        </div>
                    ) : displayShopPayButton &&
                      !disabled &&
                      !isAddToCartOnly ? (
                        <ShopPayCTAButton
                            onClick={handleShopPayClick}
                            loader={true}
                            fullWidth
                            pulse={ctaPulseEnabled}
                        />
                    ) : (
                        <CTAButton
                            backgroundColor={props.backgroundColor}
                            textColor={props.textColor}
                            fontWeight={props.fontWeight}
                            fontSize={props.fontSize}
                            // Don't show loading spinner for ATC since cart drawer opens
                            loader={!isAddToCartOnly}
                            padding={props.padding ?? '13px 0px'}
                            onClick={
                                isAddToCartOnly
                                    ? handleAddToCartClick
                                    : handleBuyNowClick
                            }
                            buttonText={mainButtonText}
                            fitLabel={
                                isAddToCartOnly
                                    ? undefined
                                    : productButton.buttonText
                            }
                            disabled={disabled}
                            pulse={ctaPulseEnabled}
                        />
                    )}
                </div>
            </div>
            {hasVisibleBrandBenefits && (
                <BenefitsCarouselViewport
                    paddingBottom={
                        STICKY_FOOTER_BENEFITS_CAROUSEL_PADDING_BOTTOM
                    }
                >
                    {brandBenefits.map((benefit, index) => (
                        <BenefitSlide
                            key={benefit.id}
                            isActive={index === benefitsCarouselIndex}
                            delay={index === benefitsCarouselIndex ? 0.3 : 0}
                            aria-hidden={index !== benefitsCarouselIndex}
                        >
                            <BenefitSlideContent
                                benefit={benefit}
                                showIcon={true}
                                iconSize={22}
                            />
                        </BenefitSlide>
                    ))}
                </BenefitsCarouselViewport>
            )}
        </div>
    )
}

/**
 * Main landing page button component that routes to appropriate button implementations
 * based on button type. Delegates BUY_NOW and ADD_TO_CART logic to LPMainProductActionButton.
 */
export const LPMainButton: React.FC<LPMainButtonProps> = (props) => {
    const { landingPage } = useContext(ViddyLPContext)
    const ctaPulseEnabled = landingPage.ctaPulseEnabled ?? false
    const { setIsGridExpanded, setActiveLPLink } = useContext(PageContext)
    const { fetchAndCacheLinksForHashes } = useContext(LandingPageLinksContext)
    const landingPageLinks = landingPage.landingPageLinks ?? []

    const handleClick = async () => {
        if (!landingPage.button) return
        switch (landingPage.button.type) {
            case 'LINK':
                window.open(landingPage.button.url, '_blank')
                break
            case 'PDP': {
                const [lpLink] = await fetchAndCacheLinksForHashes([
                    landingPage.button.linkHash,
                ])
                if (lpLink) {
                    setActiveLPLink(lpLink)
                }
                break
            }
            case 'PLP':
                if (landingPageLinks.length > 1) {
                    setIsGridExpanded(true)
                    setActiveLPLink(null)
                } else if (landingPageLinks.length === 1) {
                    setIsGridExpanded(false)
                    setActiveLPLink(landingPageLinks[0])
                } else {
                    setIsGridExpanded(true)
                    setActiveLPLink(null)
                }
                break
        }
    }

    if (!landingPage.button || landingPage.button.type === LP_BUTTON_TYPE.NONE)
        return null

    // Per-page LPButton colors beat the brand CTA fallback inside CTAButton;
    // explicit props beat both.
    const colorProps = {
        ...props,
        backgroundColor:
            props.backgroundColor ?? landingPage.button.backgroundColor,
        textColor: props.textColor ?? landingPage.button.fontColor,
    }

    // Delegate BUY_NOW and ADD_TO_CART to specialized component
    if (isCartActionButtonType(landingPage.button.type)) {
        return <LPMainProductActionButton {...colorProps} />
    }

    // Handle other button types
    return (
        <div
            style={{
                margin: STICKY_FOOTER_NON_CART_ACTION_MARGIN,
                width: '100%',
                paddingBottom: getStickyFooterSafeAreaPadding(false),
            }}
        >
            <CTAButton
                backgroundColor={colorProps.backgroundColor}
                textColor={colorProps.textColor}
                fontWeight={props.fontWeight}
                fontSize={props.fontSize}
                loader={props.loader || false}
                padding={props.padding ?? '13px 0px'}
                onClick={handleClick}
                buttonText={landingPage.button.buttonText}
                disabled={false}
                pulse={ctaPulseEnabled}
            />
        </div>
    )
}

function getFitRemeasureKey(
    buttonText: string | React.ReactElement | null,
    fitLabel?: string,
): string | undefined {
    if (fitLabel) {
        return fitLabel
    }
    return typeof buttonText === 'string' ? buttonText : undefined
}

const CTAButtonLabel: React.FC<{
    buttonText: string | React.ReactElement
    contentRef: React.Ref<HTMLSpanElement>
    disabled: boolean
    isLoading: boolean
    textColor: string
}> = ({ buttonText, contentRef, disabled, isLoading, textColor }) => {
    if (isLoading) {
        return <ButtonLoadingSpinner color={textColor} />
    }
    if (disabled) {
        return <>Out of Stock</>
    }
    return (
        <span
            ref={contentRef}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '100%',
                minWidth: 0,
                whiteSpace: 'nowrap',
            }}
        >
            {buttonText}
        </span>
    )
}

export const CTAButton: React.FC<CTAButtonProps> = ({
    backgroundColor,
    textColor,
    border,
    onClick,
    buttonText,
    fitLabel,
    padding,
    fontWeight,
    fontSize,
    height,
    width,
    disabled = false,
    loader = true,
    pulse = false,
}) => {
    const { primaryCtaFillColor, primaryCtaTextColor } =
        useContext(CustomizationContext)
    backgroundColor = backgroundColor || primaryCtaFillColor
    textColor = textColor || primaryCtaTextColor
    fontWeight = fontWeight ?? 500
    fontSize = fontSize ?? 16
    const computedBorder = disabled
        ? 'none'
        : (border ?? `var(${PRIMARY_CTA_BORDER_VAR})`)

    const [isLoading, setIsLoading] = useState(false)
    const fittingEnabled = !disabled && !isLoading
    const {
        containerRef,
        contentRef,
        fontSize: fittedFontSize,
    } = useFitToWidth({
        defaultFontSize: fontSize,
        enabled: fittingEnabled,
        remeasureKey: getFitRemeasureKey(buttonText, fitLabel),
    })

    useEffect(() => {
        const handlePageShow = () => {
            setIsLoading(false)
        }
        window.addEventListener('pageshow', handlePageShow)
        return () => {
            window.removeEventListener('pageshow', handlePageShow)
        }
    }, [])

    const _onClick = () => {
        if (loader) {
            setIsLoading(true)
        }
        onClick()
    }

    if (!buttonText) return null

    return (
        <CTAButtonDiv
            ref={containerRef}
            pulse={!disabled && pulse}
            fillColor={backgroundColor}
            labelColor={textColor}
            disabled={disabled}
            onClick={disabled ? () => {} : _onClick}
            style={{
                ...getTypographyRoleStyle('buttonText', { fontWeight }),
                cursor: disabled ? 'not-allowed' : 'pointer',
                border: computedBorder,
                padding,
                borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
                fontSize: fittingEnabled ? fittedFontSize : fontSize,
                height: height || 'fit-content',
                width: width || '100%',
                minWidth: 0,
            }}
        >
            <CTAButtonLabel
                buttonText={buttonText}
                contentRef={contentRef}
                disabled={disabled}
                isLoading={isLoading}
                textColor={textColor}
            />
        </CTAButtonDiv>
    )
}
